// Adapter: v4 /v4/context/me payload -> legacy (v3-like) userContext shape

const PROGRAM_MAP = {
  K5P: 'k5SelDigital',
  MSP: 'mspDigital',
  HSP: 'highSchool',
}

const ACCESS_CODE_MAP = {
  k5SelDigital: 'k5SelDigital',
  mspDigital: 'mspDigital',
  highSchool: 'highSchool',
}

const toCamelCaseName = name => {
  if (typeof name !== 'string' || !name.length) return name
  return name.charAt(0).toLowerCase() + name.slice(1)
}

const normalizeClaims = claims => {
  if (!Array.isArray(claims)) return []
  return claims
    .filter(Boolean)
    .map(c => (typeof c === 'string' ? c.toLowerCase() : c))
}

const programKeyFrom = pref => {
  const { programCode, accessCode } = pref || {}
  if (programCode && PROGRAM_MAP[programCode]) return PROGRAM_MAP[programCode]
  if (accessCode && ACCESS_CODE_MAP[accessCode])
    return ACCESS_CODE_MAP[accessCode]
  return null
}

const mapPreferenceName = (name, assignmentType) => {
  // Explicit remaps first
  if (name === 'MindYeti') return 'mindYeti'
  if (name === 'TIAssessment') return 'tiAssessment'
  if (name === 'BullyingHarassmentUnitEnabled')
    return 'bullyingHarassmentUnitEnabled'
  if (name === 'Regions') return 'regions'
  if (name === 'StudentActivities') return 'studentActivities'
  if (name === 'ProgramIsEnabled') return 'programIsEnabled'
  if (name === 'ImplementationType') return 'implementationType'
  if (name === 'ImplementationLevel') return 'implementationLevel'
  // SiteUser-specific mapping
  if (assignmentType === 'SiteUser' && name === 'ProgramTrack')
    return 'programTrack'
  // Fallback camelCase
  return toCamelCaseName(name)
}

const coerceValue = (key, value) => {
  if (key === 'implementationLevel') {
    const num = typeof value === 'string' ? Number(value) : value
    return Number.isFinite(num) ? num : value
  }
  return value
}

const attachCourses = (programs, v4Courses) => {
  if (!v4Courses) return
  // Support either object keyed by program code or a flat array scoped to HS
  if (Array.isArray(v4Courses)) {
    // Assume HS courses only when array provided
    programs.highSchool = programs.highSchool || {}
    programs.highSchool.courses = v4Courses.map(c => ({
      name: c?.name,
      version: c?.version ?? null,
    }))
    return
  }
  // Object keyed by program code (e.g., { HSP: [{...}], MSP: [...] })
  Object.entries(v4Courses).forEach(([code, list]) => {
    const programKey = PROGRAM_MAP[code] || ACCESS_CODE_MAP[code] || null
    if (!programKey) return
    programs[programKey] = programs[programKey] || {}
    programs[programKey].courses = Array.isArray(list)
      ? list.map(c => ({
          name: c?.name,
          version: c?.version ?? null,
        }))
      : []
  })
}

export const adaptContextV4ToLegacyContext = v4 => {
  if (!v4 || typeof v4 !== 'object') return { sites: [] }

  // If payload already looks legacy (sites array of objects with programs), return as-is
  if (Array.isArray(v4.sites)) {
    const looksLegacy = v4.sites.every(
      s => s && (s.siteId != null || s.site_id != null),
    )
    if (looksLegacy) return v4
  }

  // Normalize sites to array, preserving object keys as siteIds when provided as a map
  const sitesArray = Array.isArray(v4.sites)
    ? v4.sites
    : v4.sites && typeof v4.sites === 'object'
    ? Object.entries(v4.sites).map(([key, site]) =>
        site
          ? {
              ...site,
              // Prefer numeric site id from key when the key itself is numeric; otherwise fallback
              siteId:
                (/^\d+$/.test(String(key)) ? Number(key) : undefined) ??
                site?.siteId ??
                site?.id ??
                key,
            }
          : site,
      )
    : []

  const legacySites = sitesArray
    .map(site => {
      if (!site) return null
      const siteId = site.siteId != null ? site.siteId : site.id
      const legacySite = {
        siteId: typeof siteId === 'string' ? Number(siteId) : siteId,
        claims: normalizeClaims(site.claims),
        programs: {},
      }

      // Preferences array -> program preferences
      const prefs = Array.isArray(site.preferences) ? site.preferences : []
      // Track preferences without program associations to handle after courses are attached
      const prefsWithoutProgram = []

      prefs.forEach(pref => {
        if (!pref) return
        const programKey = programKeyFrom(pref)
        if (!programKey) {
          // Store preferences without program associations for later processing
          if (pref?.assignmentType?.toLowerCase() === 'site') {
            prefsWithoutProgram.push(pref)
          }
          return
        }
        const name = mapPreferenceName(pref.name, pref.assignmentType)
        const value = coerceValue(name, pref.value)
        legacySite.programs[programKey] = legacySite.programs[programKey] || {}
        const container =
          pref?.assignmentType?.toLowerCase() === 'siteuser'
            ? (legacySite.programs[programKey].preferences =
                legacySite.programs[programKey].preferences || {})
            : (legacySite.programs[programKey].preferences =
                legacySite.programs[programKey].preferences || {})
        container[name] = value
      })

      // Attach courses if present
      attachCourses(legacySite.programs, site.courses)

      // Handle Site-level preferences without program associations
      // Store them in all programs that exist on the site
      if (prefsWithoutProgram.length > 0) {
        const existingProgramKeys = Object.keys(legacySite.programs)
        prefsWithoutProgram.forEach(pref => {
          const name = mapPreferenceName(pref.name, pref.assignmentType)
          const value = coerceValue(name, pref.value)

          if (existingProgramKeys.length > 0) {
            // Store in all existing programs
            existingProgramKeys.forEach(programKey => {
              legacySite.programs[programKey] =
                legacySite.programs[programKey] || {}
              legacySite.programs[programKey].preferences =
                legacySite.programs[programKey].preferences || {}
              legacySite.programs[programKey].preferences[name] = value
            })
          } else {
            // If no programs exist yet, infer from courses or create a default
            // Check if we can infer from courses structure
            if (site.courses) {
              const courseProgramKeys = Array.isArray(site.courses)
                ? ['highSchool'] // HS courses come as array
                : Object.keys(site.courses)
                    .map(code => PROGRAM_MAP[code] || ACCESS_CODE_MAP[code])
                    .filter(Boolean)

              if (courseProgramKeys.length > 0) {
                courseProgramKeys.forEach(programKey => {
                  legacySite.programs[programKey] =
                    legacySite.programs[programKey] || {}
                  legacySite.programs[programKey].preferences =
                    legacySite.programs[programKey].preferences || {}
                  legacySite.programs[programKey].preferences[name] = value
                })
              }
            }
          }
        })
      }

      // HS per-site flags promoted to HS preferences when present on site
      const hsFlags = [
        'isHSSetUp',
        'isHsImplementationPlanComplete',
        'isPacingComplete',
        'areUsersAdded',
      ]
      const hsPrefMap = {
        isHSSetUp: 'isSetup',
        isHsImplementationPlanComplete: 'isImplementationPlanComplete',
        isPacingComplete: 'isPacingComplete',
        areUsersAdded: 'areUsersAdded',
      }
      hsFlags.forEach(flag => {
        if (site[flag] === undefined) return
        legacySite.programs.highSchool = legacySite.programs.highSchool || {
          preferences: {},
        }
        const prefsObj = legacySite.programs.highSchool.preferences || {}
        prefsObj[hsPrefMap[flag]] = !!site[flag]
        legacySite.programs.highSchool.preferences = prefsObj
      })

      return legacySite
    })
    .filter(Boolean)

  const legacy = {
    sites: legacySites,
    paks: v4.paks,
    location: v4.location,
    preferences: {},
  }
  return legacy
}

export default {
  adaptContextV4ToLegacyContext,
}
